// src/admin/components/ImportOrderReturnDialog.tsx
//
// Every return line now references one specific Car by id (see
// ImportOrder.ts ReturnItem.carId) — a used/individual return is always a
// single-VIN dispute, and a brand-new batch return is just several such
// lines selected at once. This replaces the old "quantity vs.
// receivedQuantity" restock picker, which never identified which physical
// vehicle was actually being disputed.
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { ImportOrder, ReturnItem, ReturnReason, ReturnStatus, RETURN_REASON_LABELS } from './ImportOrder';
import { ImportOrderReturnService } from './importOrderReturnService';
import { ProductService } from '../car/carService';
import { useAuth } from '../../../contexts/AuthContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { useCompany } from '../../../contexts/CompanyContext';
import { formatCurrency } from '../../../util/Formatcurrency';

interface POReturnDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  purchaseOrder: ImportOrder;
}

interface ReturnableUnit {
  carId: string;
  brandName: string;
  modelName: string;
  trimName?: string;
  unitPrice: number;
  warehouseId?: string;
  warehouseName?: string;
  vin: string;
  status: string;
  selected: boolean;
  reason: ReturnReason;
  alreadyReturned: boolean;
}

const POReturnDialog: React.FC<POReturnDialogProps> = ({
  open, onClose, onSuccess, purchaseOrder: po,
}) => {
  const { currentUser } = useAuth();
  const { companyProfile } = useCompany();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isDark = theme.palette.mode === 'dark';

  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);

  const [loading, setLoading] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [error, setError] = useState('');

  const [units, setUnits] = useState<ReturnableUnit[]>([]);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [images, setImages] = useState<File[]>([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoadingUnits(true);
      setError('');
      try {
        const flat: { carId: string; item: typeof po.items[number] }[] = [];
        po.items.forEach(item => (item.carIds || []).forEach(carId => flat.push({ carId, item })));

        // A carId already has a live return against it (pending/approved/
        // completed) if it appears on any non-rejected, non-cancelled return
        // for this import order — those units shouldn't be returnable again.
        const existingReturns = await ImportOrderReturnService.getReturnsForImportOrder(po.id!).catch(() => []);
        const returnedCarIds = new Set<string>();
        existingReturns.forEach(ret => {
          if (ret.status === ReturnStatus.REJECTED || ret.status === ReturnStatus.CANCELLED) return;
          ret.items.forEach(item => { if (item.carId) returnedCarIds.add(item.carId); });
        });

        const resolved = await Promise.all(flat.map(async ({ carId, item }) => {
          const car = await ProductService.getProduct(carId).catch(() => null);
          return {
            carId,
            brandName: item.brandName,
            modelName: item.modelName,
            trimName: item.trimName,
            unitPrice: item.unitPrice,
            warehouseId: car?.branchId || item.warehouseId,
            warehouseName: car?.branchName || item.warehouseName,
            vin: car?.sku || item.vin || '-',
            status: car?.status || 'Unknown',
            selected: false,
            reason: ReturnReason.OTHER,
            alreadyReturned: returnedCarIds.has(carId),
          } as ReturnableUnit;
        }));

        setUnits(resolved);
      } catch (err) {
        console.error('Error loading returnable vehicles:', err);
        setError('Failed to load vehicles for this import order');
      } finally {
        setLoadingUnits(false);
      }
      setReason('');
      setNotes('');
      setInternalNotes('');
      setImages([]);
    })();
  }, [open, po]);

  const toggleUnit = (carId: string, selected: boolean) => {
    setUnits(prev => prev.map(u => u.carId === carId ? { ...u, selected } : u));
  };

  const setUnitReason = (carId: string, unitReason: ReturnReason) => {
    setUnits(prev => prev.map(u => u.carId === carId ? { ...u, reason: unitReason } : u));
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) setImages([...images, ...Array.from(event.target.files)]);
  };

  const handleRemoveImage = (index: number) => setImages(images.filter((_, i) => i !== index));

  const selectedUnits = units.filter(u => u.selected);
  const totalAmount = selectedUnits.reduce((sum, u) => sum + u.unitPrice, 0);

  const handleSubmit = async () => {
    if (!currentUser) return;
    if (selectedUnits.length === 0) { setError('Please select at least one vehicle to return'); return; }
    if (!reason.trim()) { setError('Please provide a reason for the return'); return; }

    const itemsToReturn: ReturnItem[] = selectedUnits.map(u => ({
      id: `return-${u.carId}`,
      carId: u.carId,
      brandName: u.brandName,
      modelName: u.modelName,
      trimName: u.trimName,
      vin: u.vin,
      quantity: 1,
      unitPrice: u.unitPrice,
      totalPrice: u.unitPrice,
      reason: u.reason,
      warehouseId: u.warehouseId,
      warehouseName: u.warehouseName,
    }));

    try {
      setLoading(true);
      setError('');
      await ImportOrderReturnService.createReturn(
        { importOrderId: po.id!, items: itemsToReturn, reason, notes, internalNotes, images },
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown'
      );
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create return');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { setError(''); onClose(); };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 680, md: 800 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* Header */}
          <Box sx={{
            px: 3, py: 2,
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <Box>
              <Typography variant="h6">Create Return</Typography>
              <Typography variant="body2" color="text.secondary">{po.poNumber}</Typography>
            </Box>
            <IconButton onClick={handleClose} edge="end" disabled={loading}><CloseIcon /></IconButton>
          </Box>

          {/* Scrollable Content */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={3}>
              {/* Supplier Info */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    backgroundImage: 'none',
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Supplier Information</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{po.supplierName}</Typography>
                  <Typography variant="body2" color="text.secondary">{po.supplierEmail} | {po.supplierPhone}</Typography>
                </Paper>
              </Grid>

              {/* Return Items */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Select Vehicles to Return</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Each row is one specific vehicle generated from this import order. A used/individual return will
                  usually be a single row; a brand-new batch return can select several interchangeable units at once.
                </Typography>
                {loadingUnits ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : units.length === 0 ? (
                  <Alert severity="info">No vehicles were generated from this import order yet.</Alert>
                ) : (
                  <Paper variant="outlined" sx={{ overflow: 'auto', bgcolor: 'background.paper', backgroundImage: 'none' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>Vehicle</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Show Room</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Status</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Reason</TableCell>
                          <TableCell align="right">Cost</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {units.map((unit) => {
                          const disabled = unit.status === 'Sold' || unit.status === 'Delivered' || unit.alreadyReturned;
                          return (
                            <TableRow key={unit.carId} sx={{ opacity: disabled ? 0.5 : 1 }}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={unit.selected}
                                  onChange={(e) => toggleUnit(unit.carId, e.target.checked)}
                                  disabled={loading || disabled}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {unit.brandName} {unit.modelName}{unit.trimName ? ` ${unit.trimName}` : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">VIN: {unit.vin}</Typography>
                                {unit.warehouseName && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                                    {unit.warehouseName}
                                  </Typography>
                                )}
                                <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}>
                                  <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <Select value={unit.reason} onChange={(e) => setUnitReason(unit.carId, e.target.value as ReturnReason)} disabled={loading || disabled}>
                                      {Object.entries(RETURN_REASON_LABELS).map(([key, label]) => (
                                        <MenuItem key={key} value={key}>{label}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                {unit.warehouseName
                                  ? <Chip label={unit.warehouseName} size="small" variant="outlined" />
                                  : <Typography variant="caption" color="error">No show room</Typography>}
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <Chip
                                  label={unit.alreadyReturned ? 'Already Returned' : unit.status}
                                  size="small"
                                  variant="outlined"
                                  color={disabled ? 'error' : 'default'}
                                />
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                <FormControl size="small" sx={{ minWidth: 170 }}>
                                  <Select value={unit.reason} onChange={(e) => setUnitReason(unit.carId, e.target.value as ReturnReason)} disabled={loading || disabled}>
                                    {Object.entries(RETURN_REASON_LABELS).map(([key, label]) => (
                                      <MenuItem key={key} value={key}>{label}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: unit.selected ? 600 : 400, color: unit.selected ? 'error.main' : 'text.secondary' }}
                                >
                                  {formatCurrency(unit.unitPrice, currencySymbol)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>
                )}
              </Grid>

              {/* Total */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundImage: 'none',
                    bgcolor: isDark ? 'rgba(239,83,80,0.10)' : 'rgba(239,83,80,0.06)',
                    borderColor: isDark ? 'rgba(239,83,80,0.40)' : 'rgba(239,83,80,0.30)',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Total Return Amount:</Typography>
                    <Typography variant="h5" color="error.main" sx={{ fontWeight: 700 }}>
                      {formatCurrency(totalAmount, currencySymbol)}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

              {/* Reason */}
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Return Reason *" multiline rows={2} value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Describe the reason for this return..."
                  disabled={loading}
                />
              </Grid>

              {/* Image Upload */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Supporting Images / Inspection Report (Optional)</Typography>
                <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} sx={{ mb: 2 }} disabled={loading}>
                  Upload Images
                  <input type="file" hidden multiple accept="image/*" onChange={handleImageSelect} />
                </Button>
                {images.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {images.map((image, index) => (
                      <Chip key={index} label={image.name} onDelete={() => handleRemoveImage(index)} deleteIcon={<DeleteIcon />} />
                    ))}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Footer */}
          <Divider />
          <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
            <Button onClick={handleClose} disabled={loading} startIcon={<CancelIcon />}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="error"
              disabled={loading || totalAmount === 0}
              startIcon={<SaveIcon />}
            >
              Create Return
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ── Full-screen saving overlay ─────────────────────────────────────── */}
      <Backdrop
        open={loading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              Creating Return…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
      {/* ────────────────────────────────────────────────────────────────────── */}
    </>
  );
};

export default POReturnDialog;
